import React, { useContext, useState } from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Button, CircularProgress, TextField } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import * as Yup from "yup";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { NotificationType, SERVER_URL } from "../../constants/global";
import { UNIQUE_USER } from "../../queries/pharmacy/pharmacyTypeDefs";
import { useMutationUpdateBankAccount } from "../../queries/bankAccount/hooks/useMutationUpdateBankAccount";
import { useAuth } from "../../hooks/useAuth";
import { CardBrandIcons } from "../Checkout/CardBrandIcons";

const INK = "#1E293B";
const INK_SOFT = "#475569";
const LINE = "#E2E8F0";
const LINE_SOFT = "#F1F5F9";
const PRIMARY = "#2563EB";
const PRIMARY_DARK = "#1D4ED8";
const BODY_FONT = '"Source Sans 3", sans-serif';

const STRIPE_ELEMENT_STYLE = {
  style: {
    base: {
      fontSize: "15px",
      color: INK,
      fontFamily: "Source Sans 3, sans-serif",
      "::placeholder": { color: "#475569" },
    },
    invalid: { color: "#C62828", iconColor: "#C62828" },
  },
};

const stripeInputBoxSx = {
  border: `1px solid ${LINE}`,
  borderRadius: "8px",
  p: 1.5,
  minHeight: 48,
  width: "100%",
  boxSizing: "border-box" as const,
  bgcolor: "#FFFFFF",
  position: "relative" as const,
  zIndex: 1,
  "&:focus-within": {
    borderColor: PRIMARY,
    borderWidth: "1.5px",
    p: "11.5px",
  },
};

const stripeElementHostSx = {
  width: "100%",
  minHeight: 24,
  lineHeight: 0,
  "& .StripeElement": {
    width: "100%",
  },
};

const stripeNumberBoxSx = {
  ...stripeInputBoxSx,
  display: "flex",
  alignItems: "center",
  gap: 1,
  pr: 1.5,
};

const fieldCaptionSx = {
  mb: 0.75,
  display: "block",
  minHeight: 18,
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  color: INK_SOFT,
  fontFamily: BODY_FONT,
};

const primaryBtnSx = {
  bgcolor: PRIMARY,
  color: "#FFFFFF",
  borderRadius: "8px",
  fontWeight: 600,
  fontFamily: BODY_FONT,
  textTransform: "none" as const,
  boxShadow: "none",
  px: 2.5,
  "&:hover": { bgcolor: PRIMARY_DARK, boxShadow: "none" },
};

const outlinedBtnSx = {
  borderRadius: "8px",
  fontWeight: 600,
  fontFamily: BODY_FONT,
  textTransform: "none" as const,
  borderColor: LINE,
  color: INK_SOFT,
  "&:hover": { borderColor: "#475569", bgcolor: "#F8FAFC" },
};

interface ICardForm {
  clientSecret: string;
  bankAccountId: number;
  userId: number;
  pharmacy: {
    pharmacyId: number;
    Pharmacy?: { email?: string | null };
  };
  pharmacyUsersLoading: boolean;
  setIsEdit: (isEdit: boolean) => void;
  onCancel: () => void;
  onPaymentMethodSaved: () => Promise<void>;
  oldAccountId?: string | null;
}

type CardFormValues = {
  name: string;
  postalCode: string;
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name on card is required"),
  postalCode: Yup.string()
    .required("ZIP code is required")
    .matches(/^\d{5}(-\d{4})?$/, "Enter a valid US ZIP code"),
});

export const CardForm: React.FunctionComponent<ICardForm> = ({
  clientSecret,
  bankAccountId,
  userId,
  pharmacy,
  pharmacyUsersLoading,
  onCancel,
  onPaymentMethodSaved,
  oldAccountId,
}) => {
  const { addToast } = useContext(ToasterContext);
  const { token } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const pharmacyEmail = pharmacy?.Pharmacy?.email?.trim() || undefined;

  const [updateBankAccount, { loading }] = useMutationUpdateBankAccount({
    refetchQueries: [
      {
        query: UNIQUE_USER,
        variables: { where: { id: userId } },
        awaitRefetchQueries: true,
      },
    ],
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CardFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: { name: "", postalCode: "" },
  });

  const detachOldPaymentMethod = (newPaymentMethodId: string) => {
    if (!oldAccountId || oldAccountId === newPaymentMethodId) return;
    fetch(`${SERVER_URL}/stripe/payment-method/${oldAccountId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {
      /* best-effort */
    });
  };

  const onSubmit = async (formData: CardFormValues) => {
    if (!stripe || !elements) return;

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      addToast({ type: NotificationType.Error, title: "Card form is not ready yet." });
      return;
    }

    setIsLoading(true);
    try {
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: {
          name: formData.name.trim(),
          email: pharmacyEmail,
          address: {
            postal_code: formData.postalCode.trim(),
            country: "US",
          },
        },
      });
      if (pmError) {
        addToast({ type: NotificationType.Error, title: pmError.message || "Card was not accepted." });
        return;
      }

      if (paymentMethod?.card?.country && paymentMethod.card.country !== "US") {
        addToast({
          type: NotificationType.Error,
          title: "Only US-issued cards are accepted. Please use a different card.",
        });
        return;
      }

      const { setupIntent, error: setupError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: paymentMethod.id,
      });
      if (setupError) {
        addToast({ type: NotificationType.Error, title: setupError.message || "Card setup failed." });
        return;
      }

      if (setupIntent && setupIntent.status === "succeeded") {
        const cardBrand = paymentMethod.card?.brand || "card";
        const cardLast4 = paymentMethod.card?.last4 || "";
        const requestData: any = {
          Pharmacy: { connect: { id: pharmacy.pharmacyId } },
          User: { connect: { id: userId } },
          accountHolderName: { set: formData.name.trim() },
          accountId: { set: paymentMethod.id },
          bankName: { set: cardBrand.charAt(0).toUpperCase() + cardBrand.slice(1) },
          country: { set: "United States" },
          currency: { set: "USD" },
          last4: { set: cardLast4 },
          object: { set: "card" },
          routingNumber: { set: null },
          status: { set: "VERIFIED" },
          token: { set: setupIntent.id },
          hostedVerificationUrl: { set: null },
        };

        await updateBankAccount({
          variables: { data: requestData, where: { id: bankAccountId } },
        });

        detachOldPaymentMethod(paymentMethod.id);

        addToast({ type: NotificationType.Success, title: "Card added successfully." });
        await onPaymentMethodSaved();
      } else {
        addToast({
          type: NotificationType.Error,
          title: "Card setup did not complete. Please try again.",
        });
      }
    } catch (err: any) {
      addToast({ type: NotificationType.Error, title: err?.message || "Card setup failed." });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ fontFamily: BODY_FONT }}>
      <Box sx={{ fontSize: 13, color: INK_SOFT, mb: 2.5, lineHeight: 1.5 }}>
        Enter your US-issued card details below.
      </Box>
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <Box sx={fieldCaptionSx}>Name on card</Box>
          <Box
            sx={{
              ...stripeInputBoxSx,
              display: "flex",
              alignItems: "center",
              borderColor: errors.name ? "#C62828" : LINE,
            }}
          >
            <TextField
              size="small"
              fullWidth
              required
              placeholder="Name on card"
              variant="standard"
              InputProps={{ disableUnderline: true }}
              inputProps={{ autoComplete: "cc-name" }}
              sx={{
                m: 0,
                "& .MuiInputBase-root": { fontSize: 15, py: 0, fontFamily: BODY_FONT, color: INK },
                "& .MuiInputBase-input": { p: 0 },
              }}
              {...register("name")}
              error={!!errors.name}
            />
          </Box>
          {errors.name && (
            <Box sx={{ mt: 0.5, fontSize: 12, color: "#C62828" }}>{errors.name.message?.toString()}</Box>
          )}
        </Grid>
        <Grid item xs={12}>
          <Box sx={fieldCaptionSx}>Card number</Box>
          <Box sx={stripeNumberBoxSx}>
            <Box sx={{ ...stripeElementHostSx, flex: 1, minWidth: 0 }}>
              <CardNumberElement
                options={{
                  ...STRIPE_ELEMENT_STYLE,
                  showIcon: false,
                  placeholder: "Card number",
                }}
              />
            </Box>
            <CardBrandIcons compact />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={fieldCaptionSx}>Expiration date</Box>
          <Box sx={stripeInputBoxSx}>
            <Box sx={stripeElementHostSx}>
              <CardExpiryElement
                options={{
                  ...STRIPE_ELEMENT_STYLE,
                  placeholder: "MM / YY",
                }}
              />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={fieldCaptionSx}>Security code</Box>
          <Box sx={stripeInputBoxSx}>
            <Box sx={stripeElementHostSx}>
              <CardCvcElement
                options={{
                  ...STRIPE_ELEMENT_STYLE,
                  placeholder: "CVC",
                }}
              />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={fieldCaptionSx}>ZIP code</Box>
          <Box
            sx={{
              ...stripeInputBoxSx,
              display: "flex",
              alignItems: "center",
              borderColor: errors.postalCode ? "#C62828" : LINE,
            }}
          >
            <TextField
              size="small"
              fullWidth
              required
              placeholder="12345"
              variant="standard"
              InputProps={{ disableUnderline: true }}
              inputProps={{ inputMode: "numeric", autoComplete: "postal-code" }}
              sx={{
                m: 0,
                "& .MuiInputBase-root": { fontSize: 15, py: 0, fontFamily: BODY_FONT, color: INK },
                "& .MuiInputBase-input": { p: 0 },
              }}
              {...register("postalCode")}
              error={!!errors.postalCode}
            />
          </Box>
          {errors.postalCode && (
            <Box sx={{ mt: 0.5, fontSize: 12, color: "#C62828" }}>
              {errors.postalCode.message?.toString()}
            </Box>
          )}
        </Grid>
      </Grid>
      <Box
        sx={{
          mt: 2.5,
          mb: 3,
          px: 2,
          py: 1.5,
          borderRadius: "10px",
          bgcolor: "#F8FAFC",
          border: `1px solid ${LINE_SOFT}`,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 20, color: PRIMARY, flexShrink: 0, mt: 0.15 }} />
        <Box>
          <Box sx={{ fontWeight: 600, mb: 0.25, lineHeight: 1.3, color: INK, fontSize: 14 }}>
            Saved for future checkouts
          </Box>
          <Box sx={{ fontSize: 13, color: INK_SOFT, lineHeight: 1.5 }}>
            Your card will be stored securely and used as your default payment method when you
            place orders. You can update or replace it here anytime.
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
        <Button variant="outlined" onClick={onCancel} disabled={isLoading} sx={outlinedBtnSx}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || isLoading || loading || pharmacyUsersLoading}
          sx={primaryBtnSx}
        >
          Save Card
          {(isLoading || loading || pharmacyUsersLoading) && (
            <CircularProgress sx={{ ml: 1.5 }} size={14} color="inherit" />
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default CardForm;
